import * as $protobuf from 'protobufjs';
import { core, parseProto } from '@tock/coretypes';
import fetch from 'cross-fetch';

export const createFetchWorkflowResult =
  (type: string) =>
  (workflowInfo: core.WorkflowInfo): ApiAction => {
    const { workflowId, runId } = workflowInfo;
    return {
      TockApi: {
        getProto: `/async/temporal/workflow/${workflowId}/${runId}/results`,
        type,
        onError: '',
      },
    };
  };

/**
 * Derived from createFetchWorkflowResult, but only requires the necessary arguments
 * instead of a full WorkflowInfo object.
 */
export const createFetchWorkflowResultByIds =
  (type: string) =>
  (workflowId: string, runId: string): ApiAction => {
    return {
      TockApi: {
        getProto: `/async/temporal/workflow/${workflowId}/${runId}/results`,
        type,
        onError: '',
      },
    };
  };

/**
 * This is an async action, which is not supported OOTB by redux. This needs to be wrapped in a dispatch() call, either from:
 *     - useDispatch() for functional components
 *     - mapDispatchToProps() for class components (sorry)
 */
export const createFetchWorkflowResultFromUrl =
  (type: string, errorType: string, protoType: $protobuf.Type) =>
  async (workflowInfo: core.WorkflowInfo): Promise<BaseTockAction> => {
    try {
      const response = await fetch(workflowInfo.payloadUrl);
      const result = parseProto(await response.arrayBuffer(), protoType);
      return {
        type,
        payload: result,
      };
    } catch (error) {
      return {
        type: errorType,
        payload: error,
      };
    }
  };

export const createFetchWorkflowStatus =
  (type: string) =>
  (workflowId: string, runId: string): ApiAction => {
    return {
      TockApi: {
        getProto: `/async/temporal/workflow/${workflowId}/${runId}`,
        type,
        onError: '',
      },
    };
  };

/**
 * Workflow results may be retrieved in one of two ways:
 *
 * 1. The workflow returns the actual result as its output, which is retrieved via /async/temporal/{workflow}/{runId}/results
 * 2. If the size of the result payload is large or can be large, it is persisted to cloud storage, and results can be retrieved via a signed URL
 */
interface CreateWorkflowActionsOptions {
  statusType: string;
  resultType: string;
  errorType: string;
  // this option is only required if using method 2; a protobuf format must be specified so we can properly deserialize the persisted object
  protoType?: $protobuf.Type;
}
export const createWorkflowActions = ({
  statusType,
  resultType,
  errorType,
  protoType,
}: CreateWorkflowActionsOptions) => {
  return {
    fetchWorkflowStatus: createFetchWorkflowStatus(statusType),
    fetchWorkflowResult: protoType
      ? createFetchWorkflowResultFromUrl(resultType, errorType, protoType)
      : createFetchWorkflowResult(resultType),
  };
};
